import { type UseQueryResult, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, apiClient } from "./client";
import type {
	AdminResponse,
	ChangePasswordRequest,
	ClientDetail,
	ClientListResponse,
	ClientSummary,
	CreateClientRequest,
	CreateClientResponse,
	CreateOnboardingRequest,
	LoginRequest,
	OAuthAppListResponse,
	OAuthAppSummary,
	OAuthAppUpsertRequest,
	OnboardingListResponse,
	OnboardingLogsResponse,
	OnboardingRunDetail,
	OnboardingRunSummary,
	OnboardingWelcomeEmail,
	RotateSecretResponse,
	SnapshotResponse,
	TeardownResponse,
	UpdateClientRequest,
} from "./types";

/** Query key registry — centralised so invalidation stays accurate. */
export const queryKeys = {
	me: ["me"] as const,
	clients: ["clients"] as const,
	client: (id: string) => ["clients", id] as const,
	oauthApps: ["settings", "oauth-apps"] as const,
	onboardingRuns: ["onboarding"] as const,
	onboardingRun: (id: string) => ["onboarding", id] as const,
	onboardingLogs: (runId: string, ordinal: number) =>
		["onboarding", runId, "steps", ordinal, "logs"] as const,
};

// ------------------------------------------------------------
// Auth
// ------------------------------------------------------------

export function useMe(): UseQueryResult<AdminResponse | null, ApiError> {
	return useQuery<AdminResponse | null, ApiError>({
		queryKey: queryKeys.me,
		queryFn: async () => {
			try {
				return await apiClient.get<AdminResponse>("/auth/me");
			} catch (err) {
				// 401 = not logged in; swallow to null so the route guard
				// treats "unknown" and "logged out" the same.
				if (err instanceof ApiError && err.status === 401) return null;
				throw err;
			}
		},
		staleTime: 30_000,
	});
}

export function useLogin() {
	const qc = useQueryClient();
	return useMutation<AdminResponse, ApiError, LoginRequest>({
		mutationFn: (body) => apiClient.post<AdminResponse>("/auth/login", body),
		onSuccess: (admin) => {
			qc.setQueryData(queryKeys.me, admin);
			void qc.invalidateQueries({ queryKey: queryKeys.clients });
		},
	});
}

export function useLogout() {
	const qc = useQueryClient();
	return useMutation<unknown, ApiError, void>({
		mutationFn: () => apiClient.post<unknown>("/auth/logout"),
		onSuccess: () => {
			qc.setQueryData(queryKeys.me, null);
			qc.removeQueries({ queryKey: queryKeys.clients });
		},
	});
}

export function useChangePassword() {
	return useMutation<unknown, ApiError, ChangePasswordRequest>({
		mutationFn: (body) => apiClient.post<unknown>("/auth/change-password", body),
	});
}

// ------------------------------------------------------------
// Clients
// ------------------------------------------------------------

export function useClients(): UseQueryResult<ClientListResponse, ApiError> {
	return useQuery<ClientListResponse, ApiError>({
		queryKey: queryKeys.clients,
		queryFn: () => apiClient.get<ClientListResponse>("/clients"),
	});
}

export function useClient(id: string | undefined): UseQueryResult<ClientDetail, ApiError> {
	return useQuery<ClientDetail, ApiError>({
		queryKey: id ? queryKeys.client(id) : ["clients", "disabled"],
		queryFn: () => apiClient.get<ClientDetail>(`/clients/${id}`),
		enabled: Boolean(id),
	});
}

export function useCreateClient() {
	const qc = useQueryClient();
	return useMutation<CreateClientResponse, ApiError, CreateClientRequest>({
		mutationFn: (body) => apiClient.post<CreateClientResponse>("/clients", body),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: queryKeys.clients });
		},
	});
}

export function useUpdateClient(id: string) {
	const qc = useQueryClient();
	return useMutation<ClientSummary, ApiError, UpdateClientRequest>({
		mutationFn: (body) => apiClient.patch<ClientSummary>(`/clients/${id}`, body),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: queryKeys.clients });
			void qc.invalidateQueries({ queryKey: queryKeys.client(id) });
		},
	});
}

export function useDeleteClient() {
	const qc = useQueryClient();
	return useMutation<unknown, ApiError, string>({
		mutationFn: (id) => apiClient.delete<unknown>(`/clients/${id}`),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: queryKeys.clients });
		},
	});
}

export function useRotateSecret(id: string) {
	return useMutation<RotateSecretResponse, ApiError, void>({
		mutationFn: () => apiClient.post<RotateSecretResponse>(`/clients/${id}/rotate-secret`),
	});
}

export function usePollClient(id: string) {
	const qc = useQueryClient();
	return useMutation<SnapshotResponse, ApiError, void>({
		mutationFn: () => apiClient.post<SnapshotResponse>(`/clients/${id}/poll`),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: queryKeys.client(id) });
			void qc.invalidateQueries({ queryKey: queryKeys.clients });
		},
	});
}

// ------------------------------------------------------------
// Settings — Veridon-level OAuth apps (prep work for onboarding wizard)
// ------------------------------------------------------------

export function useOAuthApps(): UseQueryResult<OAuthAppListResponse, ApiError> {
	return useQuery<OAuthAppListResponse, ApiError>({
		queryKey: queryKeys.oauthApps,
		queryFn: () => apiClient.get<OAuthAppListResponse>("/settings/oauth-apps"),
	});
}

export interface SaveOAuthAppInput {
	vendor: string;
	body: OAuthAppUpsertRequest;
}

export function useSaveOAuthApp() {
	const qc = useQueryClient();
	return useMutation<OAuthAppSummary, ApiError, SaveOAuthAppInput>({
		mutationFn: ({ vendor, body }) =>
			apiClient.put<OAuthAppSummary>(`/settings/oauth-apps/${vendor}`, body),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: queryKeys.oauthApps });
		},
	});
}

export function useDeleteOAuthApp() {
	const qc = useQueryClient();
	return useMutation<unknown, ApiError, string>({
		mutationFn: (vendor) => apiClient.delete<unknown>(`/settings/oauth-apps/${vendor}`),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: queryKeys.oauthApps });
		},
	});
}

// ------------------------------------------------------------
// Onboarding wizard
// ------------------------------------------------------------

export function useOnboardingRuns(): UseQueryResult<OnboardingListResponse, ApiError> {
	return useQuery<OnboardingListResponse, ApiError>({
		queryKey: queryKeys.onboardingRuns,
		queryFn: () => apiClient.get<OnboardingListResponse>("/onboarding"),
	});
}

export function useOnboardingRun(
	id: string | undefined,
): UseQueryResult<OnboardingRunDetail, ApiError> {
	return useQuery<OnboardingRunDetail, ApiError>({
		queryKey: id ? queryKeys.onboardingRun(id) : ["onboarding", "disabled"],
		queryFn: () => apiClient.get<OnboardingRunDetail>(`/onboarding/${id}`),
		enabled: Boolean(id),
	});
}

export function useCreateOnboarding() {
	const qc = useQueryClient();
	return useMutation<OnboardingRunSummary, ApiError, CreateOnboardingRequest>({
		mutationFn: (body) => apiClient.post<OnboardingRunSummary>("/onboarding", body),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: queryKeys.onboardingRuns });
		},
	});
}

export function useStartOnboarding(id: string) {
	const qc = useQueryClient();
	return useMutation<OnboardingRunSummary, ApiError, void>({
		mutationFn: () => apiClient.post<OnboardingRunSummary>(`/onboarding/${id}/start`),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: queryKeys.onboardingRun(id) });
			void qc.invalidateQueries({ queryKey: queryKeys.onboardingRuns });
		},
	});
}

export function useRetryOnboardingStep(id: string) {
	const qc = useQueryClient();
	return useMutation<OnboardingRunSummary, ApiError, number>({
		mutationFn: (ordinal) =>
			apiClient.post<OnboardingRunSummary>(`/onboarding/${id}/steps/${ordinal}/retry`),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: queryKeys.onboardingRun(id) });
		},
	});
}

export function useAbortOnboarding(id: string) {
	const qc = useQueryClient();
	return useMutation<OnboardingRunSummary, ApiError, void>({
		mutationFn: () => apiClient.post<OnboardingRunSummary>(`/onboarding/${id}/abort`),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: queryKeys.onboardingRun(id) });
			void qc.invalidateQueries({ queryKey: queryKeys.onboardingRuns });
		},
	});
}

export function useOnboardingLogs(
	runId: string | undefined,
	ordinal: number | undefined,
): UseQueryResult<OnboardingLogsResponse, ApiError> {
	return useQuery<OnboardingLogsResponse, ApiError>({
		queryKey:
			runId && ordinal != null
				? queryKeys.onboardingLogs(runId, ordinal)
				: ["onboarding", "logs", "disabled"],
		queryFn: () =>
			apiClient.get<OnboardingLogsResponse>(`/onboarding/${runId}/steps/${ordinal}/logs`),
		enabled: Boolean(runId) && ordinal != null,
		staleTime: 2000,
	});
}

export function useWelcomeEmail() {
	const qc = useQueryClient();
	return useMutation<OnboardingWelcomeEmail, ApiError, string>({
		mutationFn: (runId) =>
			apiClient.get<OnboardingWelcomeEmail>(`/onboarding/${runId}/welcome-email`),
		onSuccess: (_data, runId) => {
			void qc.invalidateQueries({ queryKey: queryKeys.onboardingRun(runId) });
		},
	});
}

// ------------------------------------------------------------
// Teardown
// ------------------------------------------------------------

export interface TeardownInput {
	clientId: string;
	confirmation: string;
}

export function useTeardownClient() {
	const qc = useQueryClient();
	return useMutation<TeardownResponse, ApiError, TeardownInput>({
		mutationFn: ({ clientId, confirmation }) =>
			apiClient.post<TeardownResponse>(`/clients/${clientId}/teardown`, {
				confirmation,
			}),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: queryKeys.clients });
			void qc.invalidateQueries({ queryKey: queryKeys.onboardingRuns });
		},
	});
}
